import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Observable, of } from 'rxjs';
import { tap, catchError, pairwise, filter, map, startWith, distinctUntilChanged, shareReplay } from 'rxjs/operators';
import { StepTask } from '@telespot/domain';
import { TranslateService } from '@ngx-translate/core';
import { PipelineStore } from './pipeline.store';
import { StepStore } from './step.store';
import { LabelStore } from './label.store';
import { MethodTypeStore } from './method-type.store';
import { ProtocolScope, type LabelCategory as Category, type LabelOption as Option } from '@analysis/domain';

// These shapes now live in `@analysis/domain`, which owns them; re-exported here
// under their historical names so existing consumers keep working.
export { ProtocolScope };
export type { Category, Option };

export const colors = [
  '#838600',
  '#ab1eb8',
  '#0032e1',
  '#009621',
  '#e07700',
  '#12ece0',
  '#ecbc12',
  '#e07d8a',
  '#a49395',
  '#58c8d3',
];

export const defaultColor = '#ff4646';

export interface ProtocolState {
  scope: ProtocolScope;
  error: string | undefined;
}

const initialState: ProtocolState = {
  scope: ProtocolScope.Asset,
  error: undefined,
};

function hexToRGBA(hex: string, opacity: number): string {
  hex = hex.replace('#', '');

  if (hex.length === 8) {
    hex = hex.slice(2);
  }

  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((hexChar) => hexChar + hexChar)
      .join('');
  }

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

@Injectable()
export class ProtocolStore extends ComponentStore<ProtocolState> {
  constructor(
    private pipelineStore: PipelineStore,
    private stepStore: StepStore,
    private labelStore: LabelStore,
    private methodTypeStore: MethodTypeStore,
    private translate: TranslateService
  ) {
    super(initialState);
  }

  // #region Selectors

  public readonly scope$ = this.select((state) => state.scope);
  public readonly error$ = this.select((state) => state.error);

  // The downstream cells are OnPush, so we MUST emit a new
  // Option/Category reference when the language changes — that's what makes
  // a getter that reads `translate.currentLang` not enough.
  private readonly currentLang$: Observable<string> = this.translate.onLangChange.pipe(
    map((e) => e.lang),
    startWith(this.translate.currentLang),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  /** Pick the alias for `lang` with a fallback chain: requested → en → first. */
  private resolveAliasName(aliases: { [k: string]: { name: string } } | undefined, lang: string): string | undefined {
    if (!aliases) return undefined;
    return aliases[lang]?.name ?? aliases['en']?.name ?? Object.values(aliases)[0]?.name;
  }

  // Method type selector
  public readonly methodType$ = this.methodTypeStore.methodType$;
  public readonly methodTypeId$ = this.select(this.methodType$, (methodType) => methodType?.id);
  public readonly steps$ = this.stepStore.steps$;
  public readonly mosaicEnabled$ = this.select(this.steps$, (steps) => steps.some((step) => step.enableMosaicView));

  public readonly allPipelines$ = this.select(
    this.methodType$,
    this.pipelineStore.pipelines$,
    this.stepStore.pipelineCategories$,
    (methodType, pipelines, pipelineCategories) => {
      if (!methodType?.pipelineIds || !pipelines || !pipelineCategories) return [];

      return Object.keys(methodType.pipelineIds).map((pipelineId) => ({
        ...pipelines.find((p) => p.id === pipelineId),
        categories: [...(pipelineCategories[pipelineId] || [])],
      }));
    }
  );
  public readonly pipelines$ = this.select(
    this.allPipelines$,
    this.stepStore.categories$,
    this.stepStore.steps$,
    this.scope$,
    (pipelines, categories, steps, scope) => {
      return pipelines.filter((pipeline) => {
        return pipeline.categories.some((categoryId) => {
          const category = categories[categoryId];

          if (!category) return false;

          const step = steps.find((s) => s.id === category.stepId);
          if (!step) return false;

          return step.generateFinding?.startsWith(scope);
        });
      });
    }
  );

  // Categories selector
  public readonly categories$ = this.select(
    this.pipelines$,
    this.stepStore.categories$,
    this.stepStore.steps$,
    this.labelStore.labels$,
    this.scope$,
    this.currentLang$,
    (pipelines, categories, steps, labels, scope, lang) => {
      const categoryRecord: Record<string, Category> = {};

      if (!pipelines?.length || !categories || !steps.length || !labels.length) return categoryRecord;

      for (const pipeline of pipelines) {
        for (const categoryId of pipeline.categories) {
          const category = categories[categoryId];
          const step = steps.find((s) => s.id === category?.stepId);
          const label = labels.find((l) => l.id === category?.id);
          const stepWithinScope = step?.generateFinding?.startsWith(scope);

          if (!step || !stepWithinScope) continue;

          categoryRecord[categoryId] = {
            ...category,
            scope,
            stepTask: step?.stepTask ?? StepTask.POSITION,
            name: this.resolveAliasName(label?.aliases, lang),
          };
        }
      }

      return categoryRecord;
    }
  );

  // All options (unfiltered by scope) - for ROI overlays
  public readonly allOptions$ = this.select(
    this.methodType$,
    this.allPipelines$,
    this.stepStore.categories$,
    this.stepStore.steps$,
    this.labelStore.labels$,
    this.currentLang$,
    (methodType, pipelines, rawCategories, steps, labels, lang) => {
      const optionRecord: Record<string, Option> = {};

      if (!methodType || !pipelines || !rawCategories || !steps || !labels) return optionRecord;

      for (const pipeline of pipelines) {
        for (const categoryId of pipeline.categories) {
          const category = rawCategories[categoryId];
          if (!category) continue;

          for (const { optionId, index } of category.options.map((optionId, index) => ({ optionId, index }))) {
            const step = steps.find((s) => s.id === category.stepId);
            const label = labels.find((l) => l.id === optionId);
            const config = methodType.labelConfig?.[optionId];

            if (!label || !step) continue;

            const color =
              config?.color && config.color.length >= 7 ? config.color : colors[index % colors.length] ?? defaultColor;

            optionRecord[optionId] = {
              ...label,
              name: this.resolveAliasName(label.aliases, lang),
              color: hexToRGBA(color, 1),
              threshold: Number(step?.thresholds?.[optionId]?.['thrdisp'] ?? 0),
            };
          }
        }
      }

      return optionRecord;
    }
  );

  // Options selector (scope-filtered)
  public readonly options$ = this.select(
    this.methodType$,
    this.categories$,
    this.stepStore.steps$,
    this.labelStore.labels$,
    this.scope$,
    this.currentLang$,
    (methodType, categories, steps, labels, scope, lang) => {
      const optionRecord: Record<string, Option> = {};

      if (!methodType || !categories || !steps || !labels) return optionRecord;

      for (const categoryId in categories) {
        const category = categories[categoryId];

        for (const { optionId, index } of category.options.map((optionId, index) => ({ optionId, index }))) {
          const step = steps.find((s) => s.id === category.stepId);
          const label = labels.find((l) => l.id === optionId);
          const config = methodType.labelConfig[optionId];
          const stepWithinScope = step?.generateFinding?.startsWith(scope);

          if (!label || !step || !stepWithinScope) continue;

          const color =
            config?.color && config.color.length >= 7 ? config.color : colors[index % colors.length] ?? defaultColor;

          optionRecord[optionId] = {
            ...label,
            name: this.resolveAliasName(label.aliases, lang),
            color: hexToRGBA(color, 1),
            threshold: Number(step?.thresholds?.[optionId]?.['thrdisp'] ?? 0),
          };
        }
      }

      return optionRecord;
    }
  );

  // Loading selector
  public readonly loading$ = this.select(
    this.stepStore.loading$,
    this.methodTypeStore.loading$,
    this.pipelineStore.loading$,
    this.labelStore.loading$,
    (stepLoading, methodTypeLoading, pipelineLoading, labelLoading) =>
      stepLoading || methodTypeLoading || pipelineLoading || labelLoading
  );

  public readonly protocolLoaded$ = this.loading$.pipe(
    pairwise(),
    filter(([prev, curr]) => !!prev && !curr)
  );

  // Error selector
  public readonly protocolError$ = this.select(
    this.stepStore.error$,
    this.methodTypeStore.error$,
    this.pipelineStore.error$,
    this.labelStore.error$,
    (stepError, methodTypeError, pipelineError, labelError) =>
      stepError || methodTypeError || pipelineError || labelError
  );

  // Tagging tasks selector
  public readonly taggingTasks$ = this.select(this.categories$, (categories) => {
    const taggingCategories: Record<string, Category> = {};

    const categoryIds = Object.keys(categories ?? {});

    if (!categoryIds.length) return taggingCategories;

    for (const categoryId of categoryIds) {
      const isTaggingTask = [StepTask.POSITION, StepTask.ROIDETECTION, StepTask.ROITRACKING].includes(
        categories[categoryId]?.stepTask
      );

      if (!isTaggingTask) continue;

      taggingCategories[categoryId] = categories[categoryId];
    }

    return taggingCategories;
  });

  // Has tagging tasks selector
  public readonly hasTaggingTasks$ = this.select(
    this.taggingTasks$,
    (taggingTasks) => Object.keys(taggingTasks).length > 0
  );

  // Segmentation tasks selector
  public readonly segmentationTasks$ = this.select(this.categories$, (categories) => {
    const segmentationCategories: Record<string, Category> = {};

    const categoryIds = Object.keys(categories ?? {});

    if (!categoryIds.length) return segmentationCategories;

    for (const categoryId of categoryIds) {
      const isSegmentationTask = categories[categoryId]?.stepTask === StepTask.SEGMENTATION;

      if (!isSegmentationTask) continue;

      segmentationCategories[categoryId] = categories[categoryId];
    }

    return segmentationCategories;
  });

  // Has segmentation tasks selector
  public readonly hasSegmentationTasks$ = this.select(
    this.segmentationTasks$,
    (segmentationTasks) => Object.keys(segmentationTasks).length > 0
  );

  // Tagging pipelines selector
  public readonly taggingPipelines$ = this.select(this.taggingTasks$, this.pipelines$, (taggingTasks, pipelines) => {
    const taggingTaskIdSet = new Map(Object.entries(taggingTasks ?? {}));

    return pipelines
      .map((pipeline) => {
        const filteredCategories = (pipeline.categories ?? []).filter(
          (categoryId) => taggingTaskIdSet.has(categoryId) && taggingTasks[categoryId]?.scope === ProtocolScope.Asset
        );

        if (filteredCategories.length === 0) return null;

        return { ...pipeline, categories: filteredCategories };
      })
      .filter(Boolean);
  });

  // Acquisition fields selector
  public readonly acquisitionFields$ = this.select(
    this.methodType$,
    (methodType) => methodType?.acquisitionFields || []
  );

  // #endregion

  // #region Updaters

  public readonly setScope = this.updater((state, scope: ProtocolScope) => ({
    ...state,
    scope,
  }));

  public readonly setError = this.updater((state, error: string) => ({
    ...state,
    error,
  }));

  public readonly clearError = this.updater((state) => ({
    ...state,
    error: undefined,
  }));

  // #endregion

  // #region Effects

  public readonly selectSample = this.effect((sampleId$: Observable<string>) =>
    sampleId$.pipe(
      tap((sampleId: string) => {
        this.clearError();
        this.methodTypeStore.findForSample(sampleId);
        this.pipelineStore.findForSample(sampleId);
        this.stepStore.findForSample(sampleId);
      }),
      catchError((error: string) => {
        this.setError(error);
        return of(null);
      })
    )
  );

  // #endregion
}
